import { useState } from 'react'
import { ActionIcon, Button, Group, Menu, Text, UnstyledButton } from '@mantine/core'
import type { DateValue } from '@mantine/dates'
import { modals } from '@mantine/modals'
import {
  IconChevronDown,
  IconCornerDownRight,
  IconMessagePlus,
  IconTrash,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import type {
  AssistantDebugSelection,
  AssistantProviderName,
  AssistantProviderOption,
  AssistantConversationResponse,
  ConversationSummary,
} from '../useAssistant'
import { AssistantDebugSettings } from './AssistantDebugSettings'

dayjs.extend(relativeTime)

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
})

function formatStart(iso: string): string {
  return dateFormatter.format(new Date(iso))
}

function formatRelative(iso: string): string {
  return dayjs(iso).fromNow()
}

function samePath(a: string[] | null | undefined, b: string[] | null | undefined): boolean {
  if (!a || !b) return false
  return a.length === b.length && a.every((seg, i) => seg === b[i])
}

type Props = {
  conversations: ConversationSummary[]
  conversationId: string | null
  activeThreadPath: string[] | null
  conversationKey: string | null
  debugTools: boolean
  debugProviders: AssistantProviderOption[]
  currentDebugSelection: AssistantDebugSelection
  selectedDebugSelection: AssistantDebugSelection
  revealConversationDetails: boolean
  conversationCostUsd?: number
  forceTodayDate: boolean
  forcedTodayDate: DateValue
  actors: string[]
  selectedActor: string | null
  selfActor: string | null
  showActorPicker: boolean
  onActorChange: (actor: string) => void
  onSelect: (threadPath: string[]) => void
  onOpen: () => void
  onDelete: (conversationId: string) => void
  onNewChat: () => void
  onDebugProviderChange: (provider: AssistantProviderName | null) => void
  onDebugModelChange: (model: string | null) => void
  onRevealConversationDetailsChange: (value: boolean) => void
  onForceTodayDateChange: (value: boolean) => void
  onForcedTodayDateChange: (value: DateValue) => void
  onDownloadConversation: () => Promise<AssistantConversationResponse | null>
}

export const ChatHeader = ({
  conversations,
  conversationId,
  activeThreadPath,
  conversationKey,
  debugTools,
  debugProviders,
  currentDebugSelection,
  selectedDebugSelection,
  revealConversationDetails,
  conversationCostUsd,
  forceTodayDate,
  forcedTodayDate,
  actors,
  selectedActor,
  selfActor,
  showActorPicker,
  onActorChange,
  onSelect,
  onOpen,
  onDelete,
  onNewChat,
  onDebugProviderChange,
  onDebugModelChange,
  onRevealConversationDetailsChange,
  onForceTodayDateChange,
  onForcedTodayDateChange,
  onDownloadConversation,
}: Props) => {
  const [menuOpened, setMenuOpened] = useState(false)
  const current = conversations.find((c) => c.conversationId === conversationId)
  const activeSubThread =
    activeThreadPath && activeThreadPath.length > 1
      ? current?.threads.find((t) => samePath(t.threadPath, activeThreadPath))
      : undefined
  const buttonLabel = activeSubThread
    ? `↳ ${activeSubThread.agentId || 'sub-agent'}`
    : current
      ? `${formatStart(current.createdAt)} (${formatRelative(current.createdAt)})`
      : 'New chat'

  const confirmDelete = (conversation: ConversationSummary) => {
    setMenuOpened(false)
    modals.openConfirmModal({
      title: 'Delete conversation permanently?',
      children: (
        <Text size="sm">
          This will permanently delete the conversation from BeAssistant. This action is
          irreversible.
        </Text>
      ),
      labels: { confirm: 'Delete permanently', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => onDelete(conversation.conversationId),
    })
  }

  return (
    <div className="flex h-[65px] items-center justify-between border-b border-[#e2e8f0] bg-white px-5">
      <Group gap="sm" wrap="nowrap">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#2563eb] to-[#7c3aed] text-sm font-bold text-white">
          ✨
        </div>
        <div>
          <Text size="sm" fw={700} className="!text-slate-900">
            BeAssistant
          </Text>
          <Text size="xs" c="dimmed">
            Ask anything about patients, appointments, billing & operations
          </Text>
        </div>
      </Group>
      <Group gap="xs" wrap="nowrap">
        {debugTools && (
          <AssistantDebugSettings
            providers={debugProviders}
            conversationId={conversationId}
            conversationKey={conversationKey}
            currentSelection={currentDebugSelection}
            selectedSelection={selectedDebugSelection}
            revealConversationDetails={revealConversationDetails}
            conversationCostUsd={conversationCostUsd}
            forceTodayDate={forceTodayDate}
            forcedTodayDate={forcedTodayDate}
            actors={actors}
            selectedActor={selectedActor}
            selfActor={selfActor}
            showActorPicker={showActorPicker}
            onActorChange={onActorChange}
            onProviderChange={onDebugProviderChange}
            onModelChange={onDebugModelChange}
            onRevealConversationDetailsChange={onRevealConversationDetailsChange}
            onForceTodayDateChange={onForceTodayDateChange}
            onForcedTodayDateChange={onForcedTodayDateChange}
            onDownloadConversation={onDownloadConversation}
          />
        )}
        <Menu
          position="bottom-end"
          shadow="md"
          width={280}
          disabled={conversations.length === 0}
          opened={menuOpened}
          onChange={(opened) => {
            // Re-query on open: a sub-agent thread may have been spawned since
            // the list was last fetched, so the dropdown reflects it.
            if (opened) onOpen()
            setMenuOpened(opened)
          }}
        >
          <Menu.Target>
            <UnstyledButton
              className="flex items-center gap-1 rounded-md border border-[#e2e8f0] bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 disabled:cursor-default disabled:opacity-60"
              disabled={conversations.length === 0}
            >
              <span>{buttonLabel}</span>
              <IconChevronDown size={14} />
            </UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown className="max-h-[66vh] overflow-y-auto">
            <Menu.Label>Recent conversations</Menu.Label>
            {conversations.map((c) => {
              const rootPath = [c.conversationId]
              const rootSelected = samePath(rootPath, activeThreadPath)
              return (
                <div key={c.conversationId}>
                  <div
                    className={`group flex items-center gap-1 border-l-2 px-1 py-0.5 ${
                      rootSelected
                        ? 'border-l-[#2563eb] bg-blue-50'
                        : 'border-l-transparent hover:bg-slate-50'
                    }`}
                  >
                    <UnstyledButton
                      className={`min-w-0 flex-1 rounded px-2 py-1.5 text-left ${
                        rootSelected ? 'font-semibold text-[#1d4ed8]' : ''
                      }`}
                      onClick={() => {
                        setMenuOpened(false)
                        onSelect(rootPath)
                      }}
                    >
                      <Text size="sm">{formatStart(c.createdAt)}</Text>
                      <Text size="xs" c={rootSelected ? '#2563eb' : 'dimmed'}>
                        ({formatRelative(c.createdAt)})
                      </Text>
                    </UnstyledButton>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="red"
                      aria-label="Delete conversation"
                      title="Delete conversation"
                      className="opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
                      onClick={() => confirmDelete(c)}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </div>
                  {/* Nested sub-agent threads, shown only when "Reveal
                      conversation details" is enabled in debug settings.
                      Indented by depth, selectable like a room, and
                      intentionally NOT deletable — the delete API only accepts a
                      root convId and cascades sub-threads with the parent. */}
                  {revealConversationDetails &&
                    c.threads.map((t) => {
                      const selected = samePath(t.threadPath, activeThreadPath)
                      const depth = t.threadPath.length - 1
                      const leafId = t.threadPath[t.threadPath.length - 1]
                      return (
                        <div
                          key={t.threadPath.join(':')}
                          className={`flex items-center gap-1 border-l-2 py-0.5 pr-1 ${
                            selected
                              ? 'border-l-[#2563eb] bg-blue-50'
                              : 'border-l-transparent hover:bg-slate-50'
                          }`}
                          style={{ paddingLeft: 8 + depth * 16 }}
                        >
                          <UnstyledButton
                            className={`min-w-0 flex-1 rounded px-2 py-1 text-left ${
                              selected ? 'font-semibold text-[#1d4ed8]' : ''
                            }`}
                            onClick={() => {
                              setMenuOpened(false)
                              onSelect(t.threadPath)
                            }}
                          >
                            <Group gap={4} wrap="nowrap">
                              <IconCornerDownRight
                                size={12}
                                className={`shrink-0 ${selected ? 'text-[#2563eb]' : 'text-slate-400'}`}
                              />
                              <Text size="xs" className="truncate">
                                {t.agentId || 'sub-agent'}
                              </Text>
                            </Group>
                            <Text size="xs" c="dimmed" className="truncate pl-4">
                              {leafId}
                            </Text>
                          </UnstyledButton>
                        </div>
                      )
                    })}
                </div>
              )
            })}
          </Menu.Dropdown>
        </Menu>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconMessagePlus size={14} />}
          onClick={onNewChat}
        >
          New
        </Button>
      </Group>
    </div>
  )
}
